/**
 * Single editable row in the Binom paths list. Inline weight input + active
 * switch — matches the Voluum DefaultPathListRow chrome exactly so the drawer
 * feels identical between providers.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { Switch } from '@/components/ui/form/controls/Switch';
import { cn } from '@/lib/utils';
import {
  usePreviewMode,
  usePreviewRow,
} from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import PreviewAwareWeightSlot from '@/features/flow-optimizer/components/preview/PreviewAwareWeightSlot';
import PreviewExcludedIndicator from '@/features/flow-optimizer/components/preview/PreviewExcludedIndicator';
import { FlowTagChips } from '@/features/flow-optimizer/components/preview/FlowTagChips';
import { PathOfferCapChips } from '@/features/offer-caps';
import { excludedReasonText, showExcludedBadge } from '@/features/flow-optimizer/components/preview/preview-row.types';

import type { BinomDraftPath } from '../../binom-flow-draft.types';

export interface BinomPathListRowProps {
  path: BinomDraftPath;
  selected: boolean;
  weightPercent: number;
  onSelect: () => void;
  onPatch: (mutator: (p: BinomDraftPath) => void) => void;
}

const BinomPathListRow: FC<BinomPathListRowProps> = ({
  path,
  selected,
  weightPercent,
  onSelect,
  onPatch,
}) => {
  const previewMode = usePreviewMode();
  const previewRow = usePreviewRow('paths', path.id);
  const previewLocked =
    previewMode && previewRow != null && !showExcludedBadge(previewRow, path.active);
  const gridCols = previewLocked
    ? 'grid-cols-[minmax(0,1fr)_3rem_0.75rem_3rem_3.25rem_2.75rem]'
    : 'grid-cols-[minmax(0,1fr)_3.5rem_5rem_2.75rem]';

  const hoverName = path.name?.trim() ? path.name : `Path ${path.id ?? ''}`.trim();

  return (
      <div
        role="option"
        aria-selected={selected}
        className={cn(
          'transition-colors outline-none',
          selected && 'bg-primary/5',
          !path.active && 'opacity-70',
        )}
      >
      <div
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
        className="w-full px-1.5 py-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
      >
        <div className={cn('grid w-full items-center gap-x-1.5 gap-y-0', gridCols)}>
          {/* The name keeps ≥5rem and takes the rest of the line; the chips stay
                whole and drop to a second line only when the row is too narrow. */}
            <span className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5">
            <span className="min-w-0 flex-[1_1_5rem] truncate text-xs font-medium leading-tight text-foreground">
              {path.name?.trim() ? path.name : 'Untitled path'}
            </span>
            <FlowTagChips layer="paths" itemId={path.id} className="shrink-0 gap-1" />
            <PathOfferCapChips platform="binom" offerIds={path.offers.map((o) => o.id)} />
            {showExcludedBadge(previewRow, path.active) ? (
              <PreviewExcludedIndicator
                title={excludedReasonText(previewRow)}
                row={previewRow}
                name={hoverName}
              />
            ) : null}
          </span>
          <div
            className={cn('flex items-center justify-end', previewLocked && 'col-span-4')}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <PreviewAwareWeightSlot
              name={hoverName}
              previewRow={previewRow}
              previewMode={previewMode}
              active={path.active}
              value={path.weight}
              onChange={(v) =>
                onPatch((p) => {
                  p.weight = v;
                })
              }
              ariaLabel={`Weight for ${path.name || path.id}`}
            />
          </div>
          {!previewLocked ? (
            <div
              className={cn(
                'flex min-h-[1.25rem] items-center justify-end text-right text-[11px] leading-none text-muted-foreground',
                path.active && 'tabular-nums',
              )}
              title={
                path.active
                  ? undefined
                  : 'Traffic share is not applied while this path is inactive'
              }
            >
              {path.active ? `${weightPercent.toFixed(2)}%` : 'N/A'}
            </div>
          ) : null}
          <div
            className="flex items-center justify-end"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Switch
              checked={path.active}
              onCheckedChange={(v) =>
                onPatch((p) => {
                  p.active = v;
                })
              }
              aria-label={`${path.active ? 'Disable' : 'Enable'} ${path.name || path.id}`}
            />
          </div>
        </div>
      </div>
      </div>
  );
};

export default BinomPathListRow;
